<template>
  <div class="flex min-w-0 items-center" :class="containerGapClass">
    <div class="flex shrink-0 items-center justify-center" :class="logoSizeClass">
      <img :src="resolvedLogoUrl" :alt="resolvedAlt" class="h-full w-full" />
    </div>

    <div v-if="preset !== 'logo-only'" class="flex min-w-0 flex-col justify-center" :class="textOffsetClass">
      <div class="pointer-events-none absolute left-0 top-0 -z-10 opacity-0">
        <p v-if="brandName" ref="primaryMeasureRef" class="w-max" :class="primaryLineClass" :style="primaryLineStyle">
          {{ brandName }}
        </p>
        <p v-if="brandNameAlias" ref="aliasMeasureRef" class="w-max" :class="aliasLineClass" :style="aliasLineStyle">
          {{ brandNameAlias }}
        </p>
      </div>

      <p
        v-if="brandName"
        class="brand-line"
        :class="[primaryLineClass, { 'brand-line-compact': preset === 'compact' }]"
        :style="buildLineStyle(primaryLineStyle)"
      >
        <template v-if="primaryGlyphs.length <= 1">
          {{ brandName }}
        </template>
        <template v-else>
          <span
            v-for="glyph in primaryGlyphs"
            :key="glyph.key"
            class="brand-glyph"
            :class="glyph.type === 'space' ? 'brand-space' : 'shrink-0'"
            :aria-hidden="glyph.type === 'space' ? 'true' : undefined"
          >
            <template v-if="glyph.type === 'char'">{{ glyph.value }}</template>
          </span>
        </template>
      </p>

      <p
        v-if="brandNameAlias"
        class="brand-line"
        :class="[aliasLineClass, { 'brand-line-compact': preset === 'compact' }]"
        :style="buildLineStyle(aliasLineStyle)"
      >
        <template v-if="aliasGlyphs.length <= 1">
          {{ brandNameAlias }}
        </template>
        <template v-else>
          <span
            v-for="glyph in aliasGlyphs"
            :key="glyph.key"
            class="brand-glyph"
            :class="glyph.type === 'space' ? 'brand-space' : 'shrink-0'"
            :aria-hidden="glyph.type === 'space' ? 'true' : undefined"
          >
            <template v-if="glyph.type === 'char'">{{ glyph.value }}</template>
          </span>
        </template>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, type CSSProperties } from "vue";

const defaultBrandMarkUrl = "/brand-mark.svg";

type BrandLockupTheme = "light" | "dark";
type BrandLockupPreset = "default" | "compact" | "logo-only";

interface GlyphItem {
  key: string;
  type: "char" | "space";
  value: string;
}

interface BrandPresetConfig {
  logoDefaultClass: string;
  logoSingleLineClass: string;
  gapClass: string;
  textOffsetClass: string;
  titleClass: string;
  subtitleClass: string;
  englishOnlyClass: string;
  englishFontSize: number;
  englishOnlyFontSize: number;
  lineSafetyPadding: number;
}

interface ThemeTextClassConfig {
  titleClass: string;
  subtitleClass: string;
}

const props = withDefaults(defineProps<{
  brandName: string;
  brandNameAlias?: string;
  theme?: BrandLockupTheme;
  preset?: BrandLockupPreset;
  logoLightUrl?: string;
  logoDarkUrl?: string;
}>(), {
  brandNameAlias: "",
  theme: "light",
  preset: "default",
  logoLightUrl: "",
  logoDarkUrl: "",
});

const themeTextMap: Record<BrandLockupTheme, ThemeTextClassConfig> = {
  light: {
    titleClass: "text-slate-900",
    subtitleClass: "text-slate-500",
  },
  dark: {
    titleClass: "text-white",
    subtitleClass: "text-slate-400",
  },
};

const presetMap: Record<BrandLockupPreset, BrandPresetConfig> = {
  default: {
    logoDefaultClass: "h-16 w-16 rounded-2xl p-2.5",
    logoSingleLineClass: "h-14 w-14 rounded-2xl p-2.5",
    gapClass: "gap-0.5",
    textOffsetClass: "-ml-0.5 translate-y-0.5",
    titleClass: "truncate text-[1.75rem] font-black leading-none tracking-[0.02em]",
    subtitleClass: "font-semibold uppercase leading-none",
    englishOnlyClass: "font-['Arial_Black','Helvetica_Neue',Arial,sans-serif] font-black uppercase leading-none tracking-[0.04em]",
    englishFontSize: 12.4,
    englishOnlyFontSize: 18.5,
    lineSafetyPadding: 2,
  },
  compact: {
    logoDefaultClass: "h-12 w-12 rounded-xl p-2",
    logoSingleLineClass: "h-10 w-10 rounded-xl p-2",
    gapClass: "gap-0.5",
    textOffsetClass: "-ml-0.5",
    titleClass: "truncate text-lg font-black leading-[1.08] tracking-[0.02em]",
    subtitleClass: "font-semibold uppercase leading-none",
    englishOnlyClass: "font-['Arial_Black','Helvetica_Neue',Arial,sans-serif] font-black uppercase leading-none tracking-[0.04em]",
    englishFontSize: 10.5,
    englishOnlyFontSize: 12.5,
    lineSafetyPadding: 4,
  },
  "logo-only": {
    logoDefaultClass: "h-10 w-10 rounded-xl p-2",
    logoSingleLineClass: "h-10 w-10 rounded-xl p-2",
    gapClass: "gap-0",
    textOffsetClass: "",
    titleClass: "",
    subtitleClass: "",
    englishOnlyClass: "",
    englishFontSize: 10,
    englishOnlyFontSize: 10,
    lineSafetyPadding: 0,
  },
};

const primaryMeasureRef = ref<HTMLElement | null>(null);
const aliasMeasureRef = ref<HTMLElement | null>(null);
const lineWidth = ref(0);
const aliasMeasuredWidth = ref(0);

const currentTheme = computed(() => themeTextMap[props.theme]);
const currentPreset = computed(() => presetMap[props.preset]);
const hasBrandName = computed(() => Boolean(props.brandName));
const hasBrandNameAlias = computed(() => Boolean(props.brandNameAlias));
const isSingleVisibleLine = computed(() => hasBrandName.value !== hasBrandNameAlias.value);
const isPrimaryOnly = computed(() => hasBrandName.value && !hasBrandNameAlias.value);
const isAliasOnly = computed(() => !hasBrandName.value && hasBrandNameAlias.value);

/**
 * 判断一段品牌文案是否以拉丁字母为主，用于切换单英文字标样式。
 */
const isLatinBrandText = (text: string) => {
  const normalized = text.trim();

  if (!normalized) {
    return false;
  }

  const latinMatches = normalized.match(/[A-Za-z]/g) || [];
  const nonSpaceChars = normalized.replace(/\s+/g, "");

  if (!latinMatches.length || !nonSpaceChars.length) {
    return false;
  }

  return latinMatches.length / nonSpaceChars.length >= 0.6;
};

/**
 * 将品牌文案拆成可做两端分布的字元数组。
 */
const buildGlyphItems = (text: string): GlyphItem[] => {
  return Array.from(text).map((glyph, index) => ({
    key: `${glyph}-${index}`,
    type: glyph === " " ? "space" : "char",
    value: glyph,
  }));
};

const primaryGlyphs = computed(() => buildGlyphItems(props.brandName));
const aliasGlyphs = computed(() => buildGlyphItems(props.brandNameAlias));
const isPrimaryLatinOnly = computed(() => isPrimaryOnly.value && isLatinBrandText(props.brandName));
const isAliasLatinOnly = computed(() => isAliasOnly.value && isLatinBrandText(props.brandNameAlias));

const resolvedLogoUrl = computed(() => {
  if (props.theme === "dark") {
    return props.logoDarkUrl || props.logoLightUrl || defaultBrandMarkUrl;
  }

  return props.logoLightUrl || props.logoDarkUrl || defaultBrandMarkUrl;
});

const resolvedAlt = computed(() => {
  return props.brandName || props.brandNameAlias || "Brand";
});

const containerGapClass = computed(() => currentPreset.value.gapClass);
const textOffsetClass = computed(() => currentPreset.value.textOffsetClass);
const logoSizeClass = computed(() => {
  return isSingleVisibleLine.value
    ? currentPreset.value.logoSingleLineClass
    : currentPreset.value.logoDefaultClass;
});

const primaryLineClass = computed(() => {
  return isPrimaryLatinOnly.value
    ? [currentPreset.value.englishOnlyClass, currentTheme.value.titleClass]
    : [currentPreset.value.titleClass, currentTheme.value.titleClass];
});

const primaryLineStyle = computed<CSSProperties | undefined>(() => {
  return isPrimaryLatinOnly.value
    ? { fontSize: `${currentPreset.value.englishOnlyFontSize}px` }
    : undefined;
});

const aliasLineClass = computed(() => {
  return isAliasLatinOnly.value
    ? [currentPreset.value.englishOnlyClass, currentTheme.value.titleClass]
    : [currentPreset.value.subtitleClass, currentTheme.value.subtitleClass];
});

const aliasLineStyle = computed<CSSProperties>(() => {
  const baseFontSize = isAliasLatinOnly.value
    ? currentPreset.value.englishOnlyFontSize
    : currentPreset.value.englishFontSize;

  if (
    lineWidth.value > 0
    && aliasMeasuredWidth.value > lineWidth.value
  ) {
    const fittedFontSize = Math.max(
      9,
      Math.floor((baseFontSize * lineWidth.value * 100) / aliasMeasuredWidth.value) / 100,
    );

    return {
      fontSize: `${fittedFontSize}px`,
    };
  }

  return {
    fontSize: `${baseFontSize}px`,
  };
});

/**
 * 构造单行品牌字标的宽度样式，统一限制在预设上限内。
 */
const buildLineStyle = (baseStyle?: CSSProperties) => {
  return {
    width: `${Math.max(lineWidth.value + currentPreset.value.lineSafetyPadding, 1)}px`,
    ...baseStyle,
  };
};

/**
 * 同步两行测量宽度，并取较大值作为最终排版宽度。
 */
const syncLineWidth = async () => {
  await nextTick();

  if (!primaryMeasureRef.value && !aliasMeasureRef.value) {
    return;
  }

  const primaryWidth = primaryMeasureRef.value?.getBoundingClientRect().width || 0;
  const aliasWidth = aliasMeasureRef.value?.getBoundingClientRect().width || 0;
  aliasMeasuredWidth.value = aliasWidth;

  // Use primary line width as the baseline.
  // Alias line will shrink its font to fit within this width if needed.
  // Fall back to alias width when primary is absent.
  lineWidth.value = Math.ceil(primaryWidth || aliasWidth || 1);
};

onMounted(() => {
  void syncLineWidth();
});

watch(
  () => [props.brandName, props.brandNameAlias, props.preset],
  () => {
    lineWidth.value = 0;
    void syncLineWidth();
  },
);
</script>

<style scoped>
.brand-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2px 0;
  white-space: nowrap;
}

.brand-line-compact {
  margin: 1px 0;
}

.brand-space {
  display: block;
  width: 0.5em;
  height: 1px;
  flex-shrink: 0;
  margin-inline: 0.06em;
  opacity: 0;
}
</style>
